/**
 * Traitement côté client des logs d'audit pour le tableau de bord statistiques.
 *
 * Le backend n'expose pas d'endpoint de stats de connexion dédié : on s'appuie
 * sur l'API d'audit existante (`/api/admin/audit-logs/`) en filtrant les actions
 * d'authentification (`auth.login_success`, `auth.logout`), puis on agrège ici.
 * L'ambassade/direction n'étant pas incluse dans le log, on joint chaque log à
 * la liste des utilisateurs (`/api/admin/users/`).
 */
import { apiFetch } from './apiFetch'

export interface AuditLog {
  id: number
  action: string
  created_at: string
  user: { id: number; username: string; full_name: string } | null
  session_key: string | null
}

export interface UserInfo {
  id: number
  full_name: string
  role: { code: string; nom: string } | null
  direction: { id: number; code: string; nom: string } | null
  ambassade: { id: number; code: string; nom: string } | null
}

export interface UserMap {
  [id: number]: UserInfo
}

const MAX_PAGES = 25 // garde-fou : 25 * 200 = 5000 events max par action
const PER_PAGE = 200
// Plafond d'une session pour éviter les durées aberrantes (logout manquant)
const SESSION_CAP_MS = 8 * 60 * 60 * 1000 // 8 h

/** Récupère tous les logs d'une action sur une période (pagination automatique). */
export async function fetchAuditLogs(
  action: string,
  fromISO: string,
  toISO: string,
): Promise<AuditLog[]> {
  const all: AuditLog[] = []
  let page = 1
  let pages = 1
  do {
    const qs = new URLSearchParams({
      action,
      from: fromISO,
      to: toISO,
      per_page: String(PER_PAGE),
      page: String(page),
    })
    const res = await apiFetch<{ data: AuditLog[]; pagination: { pages: number } }>(
      `/api/admin/audit-logs/?${qs.toString()}`,
    )
    all.push(...(res.data || []))
    pages = res.pagination?.pages || 1
    page++
  } while (page <= pages && page <= MAX_PAGES)
  return all
}

/** Récupère la liste des utilisateurs et construit une map id -> infos. */
export async function fetchUserMap(): Promise<UserMap> {
  const users = await apiFetch<UserInfo[]>('/api/admin/users/')
  const map: UserMap = {}
  for (const u of users || []) map[u.id] = u
  return map
}

export type ScopeKind = 'ambassade' | 'direction'

/** Filtre les logs selon l'ambassade / direction / rôle / agent sélectionnés. */
export function filterLogs(
  logs: AuditLog[],
  userMap: UserMap,
  opts: {
    ambassadeId?: number | null
    directionId?: number | null
    roleCode?: string | null
    userId?: number | null
  },
): AuditLog[] {
  const { ambassadeId, directionId, roleCode, userId } = opts
  if (!ambassadeId && !directionId && !roleCode && !userId) return logs
  return logs.filter((log) => {
    if (!log.user) return false
    if (userId && log.user.id !== userId) return false
    const info = userMap[log.user.id]
    if (!info) return false
    if (ambassadeId && info.ambassade?.id !== ambassadeId) return false
    if (directionId && info.direction?.id !== directionId) return false
    if (roleCode && info.role?.code !== roleCode) return false
    return true
  })
}

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Liste de clés de jour entre deux dates incluses. */
export function dayRange(from: Date, to: Date): string[] {
  const keys: string[] = []
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate())
  while (cur <= end) {
    keys.push(dayKey(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return keys
}

/** Connexions par jour (clé jour -> nombre). */
export function loginsPerDay(logins: AuditLog[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const l of logins) {
    const k = dayKey(new Date(l.created_at))
    out[k] = (out[k] || 0) + 1
  }
  return out
}

/** Connexions par heure de la journée (0..23). */
export function loginsPerHour(logins: AuditLog[]): number[] {
  const out = new Array(24).fill(0)
  for (const l of logins) out[new Date(l.created_at).getHours()]++
  return out
}

/** Connexions par jour de la semaine (Lun..Dim). */
export function loginsPerWeekday(logins: AuditLog[]): number[] {
  // index 0 = Lundi … 6 = Dimanche
  const out = new Array(7).fill(0)
  for (const l of logins) {
    const js = new Date(l.created_at).getDay() // 0 = dimanche
    out[(js + 6) % 7]++
  }
  return out
}

/** Nombre d'utilisateurs distincts ayant une connexion. */
export function distinctUsers(logins: AuditLog[]): number {
  const set = new Set<number>()
  for (const l of logins) if (l.user) set.add(l.user.id)
  return set.size
}

/** Connexions du jour (aujourd'hui), total + utilisateurs distincts. */
export function todayStats(logins: AuditLog[]): { total: number; users: number } {
  const todayKey = dayKey(new Date())
  const today = logins.filter((l) => dayKey(new Date(l.created_at)) === todayKey)
  return { total: today.length, users: distinctUsers(today) }
}

export interface Session {
  userId: number
  start: number
  end: number
  durationMs: number
}

/**
 * Reconstitue les sessions en appariant login → logout par utilisateur.
 * On trie tous les événements auth d'un même utilisateur par date puis on
 * apparie chaque login avec le logout suivant. Les sessions sans logout (ou
 * trop longues) sont plafonnées à SESSION_CAP_MS.
 */
export function buildSessions(logins: AuditLog[], logouts: AuditLog[]): Session[] {
  type Ev = { t: number; type: 'in' | 'out'; userId: number }
  const byUser: Record<number, Ev[]> = {}
  const push = (logs: AuditLog[], type: 'in' | 'out') => {
    for (const l of logs) {
      if (!l.user) continue
      ;(byUser[l.user.id] ||= []).push({ t: new Date(l.created_at).getTime(), type, userId: l.user.id })
    }
  }
  push(logins, 'in')
  push(logouts, 'out')

  const sessions: Session[] = []
  for (const uid of Object.keys(byUser)) {
    const evs = byUser[Number(uid)].sort((a, b) => a.t - b.t)
    let openIn: number | null = null
    for (const ev of evs) {
      if (ev.type === 'in') {
        // Si une session était déjà ouverte sans logout, on la plafonne.
        if (openIn !== null) {
          sessions.push(makeSession(Number(uid), openIn, openIn + SESSION_CAP_MS))
        }
        openIn = ev.t
      } else if (ev.type === 'out' && openIn !== null) {
        const end = Math.min(ev.t, openIn + SESSION_CAP_MS)
        sessions.push(makeSession(Number(uid), openIn, end))
        openIn = null
      }
    }
    // Session encore ouverte en fin de période : plafonnée
    if (openIn !== null) sessions.push(makeSession(Number(uid), openIn, openIn + SESSION_CAP_MS))
  }
  return sessions
}

function makeSession(userId: number, start: number, end: number): Session {
  const durationMs = Math.max(0, end - start)
  return { userId, start, end, durationMs }
}

export function avgSessionMs(sessions: Session[]): number {
  if (!sessions.length) return 0
  return sessions.reduce((s, x) => s + x.durationMs, 0) / sessions.length
}

export function totalSessionMs(sessions: Session[]): number {
  return sessions.reduce((s, x) => s + x.durationMs, 0)
}

/** Top N utilisateurs par nombre de connexions. */
export function topUsers(
  logins: AuditLog[],
  userMap: UserMap,
  n = 5,
): Array<{ id: number; name: string; ambassade: string | null; count: number }> {
  const counts: Record<number, number> = {}
  for (const l of logins) if (l.user) counts[l.user.id] = (counts[l.user.id] || 0) + 1
  return Object.entries(counts)
    .map(([id, count]) => {
      const info = userMap[Number(id)]
      return {
        id: Number(id),
        name: info?.full_name || `Utilisateur ${id}`,
        ambassade: info?.ambassade?.nom || info?.direction?.nom || null,
        count,
      }
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, n)
}

/** Connexions groupées par ambassade (libellé -> nombre). */
export function loginsPerAmbassade(logins: AuditLog[], userMap: UserMap): Record<string, number> {
  const out: Record<string, number> = {}
  for (const l of logins) {
    if (!l.user) continue
    const info = userMap[l.user.id]
    const label = info?.ambassade?.nom || info?.direction?.nom || 'Non rattaché'
    out[label] = (out[label] || 0) + 1
  }
  return out
}

/** Formate une durée (ms) en "Xh Ym" / "Ym Zs". */
export function formatDuration(ms: number): string {
  if (ms <= 0) return '0 min'
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`
  return `${m} min`
}

/** Liste unique des ambassades présentes dans la map utilisateurs. */
export function listAmbassades(userMap: UserMap): Array<{ id: number; nom: string }> {
  const seen = new Map<number, string>()
  for (const id of Object.keys(userMap)) {
    const a = userMap[Number(id)].ambassade
    if (a && !seen.has(a.id)) seen.set(a.id, a.nom)
  }
  return Array.from(seen.entries()).map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom))
}

/** Liste unique des rôles présents. */
export function listRoles(userMap: UserMap): Array<{ code: string; nom: string }> {
  const seen = new Map<string, string>()
  for (const id of Object.keys(userMap)) {
    const r = userMap[Number(id)].role
    if (r && !seen.has(r.code)) seen.set(r.code, r.nom)
  }
  return Array.from(seen.entries()).map(([code, nom]) => ({ code, nom })).sort((a, b) => a.nom.localeCompare(b.nom))
}

/** Liste unique des directions présentes dans la map utilisateurs. */
export function listDirections(userMap: UserMap): Array<{ id: number; nom: string }> {
  const seen = new Map<number, string>()
  for (const id of Object.keys(userMap)) {
    const d = userMap[Number(id)].direction
    if (d && !seen.has(d.id)) seen.set(d.id, d.nom)
  }
  return Array.from(seen.entries()).map(([id, nom]) => ({ id, nom })).sort((a, b) => a.nom.localeCompare(b.nom))
}

/** Liste des utilisateurs (agents) triés par nom, pour le filtre « Agent ». */
export function listUsers(userMap: UserMap): Array<{ id: number; name: string }> {
  return Object.values(userMap)
    .map((u) => ({ id: u.id, name: u.full_name || `Utilisateur ${u.id}` }))
    .sort((a, b) => a.name.localeCompare(b.name))
}
